import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import Header from './components/common/Header';
import BottomNav from './components/common/BottomNav';
import Home from './pages/Home';
import MapPage from './pages/MapPage';
import Profile from './pages/Profile';
import Login from './pages/Login';
import Register from './pages/Register';
import Favorites from './pages/Favorites';
import ShopDetail from './components/user/ShopDetail';
import Dashboard from './components/merchant/Dashboard';
import AdminDashboard from './pages/AdminDashboard'; 
import { useTrackVisit } from './hooks/useTrackVisit';
import Loading from './components/common/Loading';
import { useShopWatcher } from './hooks/useShopWatcher';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase/config';
import { Download, X, Share } from 'lucide-react';
import OnboardingModal, { hasSeenOnboarding } from './components/common/OnboardingModal';
import ShopStoreWrapper from './pages/ShopStoreWrapper';
import PaymentReturn from './pages/PaymentReturn';

const PrivateRoute = ({ children, requireMerchant = false, requireAdmin = false }) => {
  const { user, isMerchant, isAdmin, isSuspended, loading } = useAuth();
  if (loading) return <Loading/>;
  if (!user) return <Navigate to="/login"/>;

  if (isSuspended) return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem', padding: '2rem', background: 'var(--bg)' }}>
      <div style={{ fontSize: '2.5rem' }}>🚫</div>
      <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--text)', textAlign: 'center' }}>Compte suspendu</div>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-2)', textAlign: 'center', maxWidth: 300, lineHeight: 1.5 }}>
        Votre compte a été suspendu par l'administration. Contactez le support.
      </div>
    </div>
  );

  if (requireMerchant && !isMerchant) return <Navigate to="/"/>;
  if (requireAdmin && !isAdmin) return <Navigate to="/"/>;
  return children;
};

const MerchantWatcher = () => {
  const { user, isMerchant } = useAuth();
  const [shop, setShop] = useState(null);
  useEffect(() => {
    if (!user || !isMerchant) return;
    let unsub = null;
    const init = async () => {
      try {
        const mDoc   = await getDoc(doc(db, 'merchants', user.uid));
        const shopId = mDoc.data()?.shopId;
        if (!shopId) return;
        unsub = onSnapshot(doc(db, 'shops', shopId), snap => {
          if (snap.exists()) setShop({ id: snap.id, ...snap.data() });
        });
      } catch (err) { console.error('MerchantWatcher:', err); }
    };
    init();
    return () => { if (unsub) unsub(); };
  }, [user, isMerchant]);
  useShopWatcher(shop?.managedBy === 'schedule' ? null : shop);
  return null;
};

/* ── PWA Install Prompt ─────────────────────────────────────── */
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner]         = useState(false);
  const [showIOSSheet, setShowIOSSheet]     = useState(false);
  const [isIOS, setIsIOS]                   = useState(false);

  const isStandalone = useCallback(() =>
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true, []);
  const wasDismissed = useCallback(() => {
    const ts = localStorage.getItem('yeldi_pwa_dismissed');
    return ts ? (Date.now() - parseInt(ts)) < 7 * 24 * 3600 * 1000 : false;
  }, []);

  useEffect(() => {
    if (isStandalone() || wasDismissed()) return;
    const ua = window.navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    setIsIOS(ios);
    if (ios) { const t = setTimeout(() => setShowBanner(true), 3000); return () => clearTimeout(t); }
    const handler = e => { e.preventDefault(); setDeferredPrompt(e); setTimeout(() => setShowBanner(true), 2000); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone, wasDismissed]);

  const handleInstall = async () => {
    if (isIOS) { setShowBanner(false); setShowIOSSheet(true); return; }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowBanner(false);
    setDeferredPrompt(null);
  };
  const dismiss = () => {
    setShowBanner(false); setShowIOSSheet(false);
    localStorage.setItem('yeldi_pwa_dismissed', String(Date.now()));
  };

  if (!showBanner && !showIOSSheet) return null;

  return (
    <>
      {showBanner && (
        <div className="pwa-install-banner" role="dialog" aria-label="Installer Yeldi">
          <div className="pwa-install-icon" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 30 30" fill="none">
              <path d="M15 3C8.925 3 4 7.925 4 14s4.925 11 11 11" stroke="rgba(255,255,255,.7)" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
              <circle cx="15" cy="14" r="6.5" fill="#00c9a7" opacity="0.92"/>
              <circle cx="15" cy="14" r="3.2" fill="#1b2a6b"/>
              <circle cx="16.5" cy="12.5" r="1.1" fill="white" opacity="0.95"/>
              <circle cx="9.5" cy="5.5" r="2.2" fill="rgba(255,255,255,.7)"/>
            </svg>
          </div>
          <div className="pwa-install-content">
            <strong>Installer Yeldi</strong>
            <span>Acces rapide, fonctionne hors connexion</span>
          </div>
          <div className="pwa-install-actions">
            <button className="pwa-btn-install" onClick={handleInstall}>
              {isIOS
                ? <><Share size={13} style={{ display:'inline', marginRight:3 }}/>Comment</>
                : <><Download size={13} style={{ display:'inline', marginRight:3 }}/>Installer</>
              }
            </button>
            <button className="pwa-btn-dismiss" onClick={dismiss} aria-label="Fermer">
              <X size={15} strokeWidth={2.5}/>
            </button>
          </div>
        </div>
      )}

      {showIOSSheet && (
        <>
          <div className="pwa-ios-overlay" onClick={dismiss} aria-hidden="true"/>
          <div className="pwa-ios-steps" role="dialog" aria-label="Instructions installation iOS">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem' }}>Installer Yeldi</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-2)', marginTop: '0.15rem' }}>
                  Ajoutez l'app a votre ecran d'accueil
                </div>
              </div>
              <button onClick={dismiss} aria-label="Fermer"
                style={{ width:32, height:32, borderRadius:'50%', background:'var(--bg)', border:'none', boxShadow:'var(--neu-sm)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-2)', flexShrink:0 }}>
                <X size={15} strokeWidth={2.5}/>
              </button>
            </div>
            {[
              { n:'1', title:'Appuyez sur le bouton Partager', desc:"Icone de partage dans la barre d'outils Safari (en bas ou en haut)." },
              { n:'2', title:'Selectionnez "Sur l\'ecran d\'accueil"', desc:'Faites defiler la liste des options et appuyez dessus.' },
              { n:'3', title:'Confirmez en appuyant sur "Ajouter"', desc:"L'icone Yeldi apparaitra sur votre ecran d'accueil." },
            ].map(s => (
              <div key={s.n} style={{ display:'flex', gap:'0.85rem', alignItems:'flex-start', padding:'0.55rem 0' }}>
                <div style={{
                  width:28, height:28, borderRadius:'50%', flexShrink:0,
                  background:'linear-gradient(135deg,#1b2a6b,#00c9a7)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'0.72rem', fontWeight:800, color:'#fff', marginTop:1,
                  boxShadow:'2px 2px 8px rgba(0,158,133,.35)',
                }}>
                  {s.n}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text)', marginBottom:'0.15rem' }}>{s.title}</div>
                  <div style={{ fontSize:'0.76rem', color:'var(--text-2)', lineHeight:1.5 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

/* ── AppContent ─────────────────────────────────────────────── */
function AppContent() {
  useTrackVisit(); // Compteur de visites
  const [showOnboarding, setShowOnboarding] = React.useState(() => !hasSeenOnboarding());

  return (
    <div className="app">
      <MerchantWatcher/>

      {/*
        Architecture du scroll — explication
        ──────────────────────────────────────
        Le scroll se fait sur <main> (overflowY:'auto', flex:1, height:0).
        Le Header ET la search bar de Home utilisent position:sticky avec top:0,
        ce qui fonctionne car leur ancêtre scrollable commun est <main>.

        Header : sorti du <main> et placé EN DEHORS — il est sticky dans .app
        grâce à position:sticky sur le wrapper .app qui est un flex column.
        Non : le Header est DANS le flux de .app (flex column), il reste en haut
        naturellement car <main> prend tout le reste avec flex:1.

        Résumé :
          .app          → flex column, height: 100dvh, overflow: hidden
          Header        → hauteur fixe var(--header-h), reste en haut naturellement
          <main>        → flex:1, height:0, overflowY:auto  ← le scroll est ici
          search bar    → position:sticky, top:0 dans <main>
          BottomNav     → fixed, indépendant
      */}
      <Header/>

      <main style={{
        flex: 1,
        height: 0,          /* force <main> à ne pas dépasser le flex parent */
        overflowY: 'auto',
        overflowX: 'hidden',
        /* iOS momentum scroll */
        WebkitOverflowScrolling: 'touch',
      }}>
        <Routes>
          <Route path="/"          element={<Home/>}/>
          <Route path="/map"       element={<MapPage/>}/>
          <Route path="/shop/:id"  element={<ShopDetail/>}/>
          <Route path="/favorites" element={<Favorites/>}/>
          <Route path="/profile"   element={<Profile/>}/>
          <Route path="/login"     element={<Login/>}/>
          <Route path="/register"  element={<Register/>}/>
          <Route path="/merchant"  element={<PrivateRoute requireMerchant><Dashboard/></PrivateRoute>}/>
          <Route path="/merchant/store" element={<PrivateRoute requireMerchant><ShopStoreWrapper/></PrivateRoute>}/>
          <Route path="/payment/success"  element={<PaymentReturn/>}/>
          <Route path="/admin"     element={<PrivateRoute requireAdmin><AdminDashboard/></PrivateRoute>}/>
          <Route path="*"          element={<Navigate to="/"/>}/>
        </Routes>
      </main>

      <BottomNav/>
      <PWAInstallPrompt/>
      {showOnboarding && (
        <OnboardingModal onDone={() => setShowOnboarding(false)} />
      )}
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <LocationProvider>
          <AppContent/>
        </LocationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}